import React from "react";
import moment from "moment-timezone";
import StatusIcons from "../../../../../../../Components/StatusIcons/StatusIcons";
import { convertTime } from "../../../../../../../helpers/helpers";
import { TEXT_BASE } from "../../../../../../../constants/typography";
import { marketplaceOf } from "./marketplaces";

// "8m ago", "5h ago", "3d ago", then the date ("Oct 8", or "Oct 8, 2025"
// outside the current year) in the account's time zone.
const orderAge = (createdAt) => {
  const created = moment.utc(createdAt);
  if (!createdAt || !created.isValid()) return "";

  const minutes = moment.utc().diff(created, "minutes");
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
  if (minutes < 10080) return `${Math.floor(minutes / 1440)}d ago`;

  const local = moment(convertTime(createdAt), "YYYY-MM-DD hh:mm A");
  return local.format(
    local.year() === moment().year() ? "MMM D" : "MMM D, YYYY",
  );
};

// "Archived on Nov 4, 2026": the order is out of the Orders list, its status
// unchanged.
const archivedTitle = (archivedAt) => {
  const local = moment(convertTime(archivedAt), "YYYY-MM-DD hh:mm A");
  return local.isValid()
    ? `Archived on ${local.format("MMM D, YYYY")}: out of the Orders list, status unchanged`
    : "Archived: out of the Orders list, status unchanged";
};

const Separator = () => (
  <span aria-hidden="true" className="shrink-0 px-[4px]">
    ·
  </span>
);

/**
 * Order column: Bolt's order number, the marketplace it came from and the
 * order type; underneath, the order's own number, its age and who created it
 * when that isn't the account itself. With showMarketplace={false}
 * (Warehouse Orders) there is no marketplace pill and the type always shows.
 */
const OrderCell = ({
  row,
  onOpen,
  onOpenMarketplace,
  showMarketplace = true,
}) => {
  const marketplace = showMarketplace ? marketplaceOf(row) : null;
  // Types are stored lowercase ("marketplace", "self pickup").
  const isMarketplaceType =
    String(row?.type ?? "").trim().toLowerCase() === "marketplace";
  const showType = row?.type && !(isMarketplaceType && marketplace);

  // Manual orders store the account as user and the person who typed it in
  // as created_by; synced orders have no created_by.
  const creator =
    row?.created_by?.username?.trim() || row?.user?.username?.trim();
  const isStaff =
    row?.created_by?.id != null &&
    row?.user?.id != null &&
    String(row.created_by.id) !== String(row.user.id);

  const createdAt = convertTime(row?.created_at);
  const age = orderAge(row?.created_at);
  const details = [
    `Order ${row?.order_id ?? "-"}`,
    createdAt && `created ${createdAt}`,
    creator && `by ${creator}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="flex w-full min-w-0 cursor-pointer flex-col gap-[2px]"
      onClick={() => onOpen(row)}
    >
      <div className="flex min-w-0 items-center gap-[5px]">
        <button
          type="button"
          title="Open order"
          className="shrink-0 border-0 bg-transparent p-0 font-bold text-brand hover:underline"
        >
          #{row?.id ?? "-"}
        </button>
        {marketplace && (
          <button
            type="button"
            title={`${marketplace.label} order details`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenMarketplace(row);
            }}
            className={`inline-flex shrink-0 items-center rounded border-[1px] border-transparent px-[5px] py-[3px] font-semibold ${TEXT_BASE} ${marketplace.className}`}
          >
            {marketplace.label}
          </button>
        )}
        {showType && (
          <div className="flex min-w-0">
            <StatusIcons status={row.type} noDot={true} noBorder={false} />
          </div>
        )}
        {row?.archived_at && (
          <span
            title={archivedTitle(row.archived_at)}
            className={`inline-flex shrink-0 items-center rounded border-[1px] border-ui-border bg-ui-surface-sunken px-[5px] py-[2px] font-semibold text-ui-text-muted ${TEXT_BASE}`}
          >
            Archived
          </span>
        )}
      </div>
      <div
        title={details}
        className="flex min-w-0 items-center font-normal text-ui-text-subtle"
      >
        <span className="min-w-0 truncate">{row?.order_id ?? "-"}</span>
        {age && (
          <>
            <Separator />
            <span className="shrink-0">{age}</span>
          </>
        )}
        {isStaff && (
          <>
            <Separator />
            <span className="max-w-[90px] shrink-0 truncate">{creator}</span>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderCell;
